/**
 * Where a scanned item would place in the rankings.
 *
 * WHAT THIS COMPARES AGAINST, AND WHY IT SAYS SO
 * ---------------------------------------------
 * Against the ranked products the app already holds for each board — the ones
 * fetched for the leaderboard, which the service worker has cached. Not against
 * the whole catalogue, because that would mean an API call per board per scan,
 * on a site whose whole cost model is built on not making those.
 *
 * That makes the answer a genuine but PARTIAL one, and the wording reflects it
 * exactly: landing inside the loaded set gives a real position, and landing
 * outside it gives "below the top N", never a fabricated rank. Claiming
 * "#4,181 of 10,618" from a sixty-row sample would be inventing precision.
 *
 * The scanned item is scored by the SAME `computeMetrics` the catalogue rows
 * are scored by — it is converted to a `Product` upstream precisely so that the
 * comparison is arithmetic, not an approximation of the ranking.
 */

import { computeMetrics, METRIC_DIRECTION, type MetricId } from '@/lib/nutrition/calculations';
import type { Product } from '@/types/product';

/** Boards worth previewing, and the metric each is ordered by. */
export const PREVIEW_BOARDS: Array<{ id: string; label: string; metric: MetricId }> = [
  { id: 'protein_per_dollar', label: 'Most protein per dollar', metric: 'proteinPerDollar' },
  { id: 'cheapest_per_100kcal', label: 'Cheapest per 100 kcal', metric: 'pricePer100kcal' },
  { id: 'protein_per_100kcal', label: 'Most protein per 100 kcal', metric: 'proteinPer100kcal' },
  { id: 'highest_protein', label: 'Highest protein', metric: 'protein100' },
  { id: 'cheapest_per_100g', label: 'Cheapest per 100 g', metric: 'pricePer100g' },
];

export interface PreviewPlacement {
  boardId: string;
  label: string;
  metric: MetricId;
  /** The scanned item's own value for this metric. */
  value: number;
  /** 1-based position within the compared set, or null when it falls outside. */
  rank: number | null;
  /** How many catalogue products it was compared against. */
  comparedWith: number;
  /** Total the board can rank, when known — for honest wording only. */
  eligible: number | null;
  /** True when the item beat every product in the compared set. */
  beatsAll: boolean;
}

/**
 * Place one scanned product against a set of catalogue products.
 *
 * `catalogue` is whatever the app currently holds for that board, in the
 * board's own order. Products without a value for the metric are skipped
 * rather than treated as zero — a missing value is not a bad one.
 */
export function placeAgainst(
  scanned: Product,
  catalogue: Product[],
  board: { id: string; label: string; metric: MetricId },
  eligible: number | null,
): PreviewPlacement | null {
  const own = computeMetrics(scanned).values[board.metric]?.value ?? null;
  if (own === null || !Number.isFinite(own)) return null;

  const values: number[] = [];
  for (const product of catalogue) {
    const v = computeMetrics(product).values[board.metric]?.value ?? null;
    if (v !== null && Number.isFinite(v)) values.push(v);
  }
  if (values.length === 0) return null;

  const higherIsBetter = METRIC_DIRECTION[board.metric] === 'higher';
  const better = values.filter((v) => (higherIsBetter ? v > own : v < own)).length;

  const worst = higherIsBetter ? Math.min(...values) : Math.max(...values);
  const outside = higherIsBetter ? own < worst : own > worst;

  return {
    boardId: board.id,
    label: board.label,
    metric: board.metric,
    value: own,
    // Only a position INSIDE the compared set is a real position.
    rank: outside ? null : better + 1,
    comparedWith: values.length,
    eligible,
    beatsAll: better === 0 && !outside,
  };
}

/** One sentence describing a placement, in the terms it is actually true in. */
export function describePlacement(p: PreviewPlacement): string {
  if (p.rank === null) {
    return `Below the top ${p.comparedWith.toLocaleString()} shown`;
  }
  if (p.beatsAll) {
    return `Would top the ${p.comparedWith.toLocaleString()} shown`;
  }
  return `Would enter at #${p.rank.toLocaleString()} of the ${p.comparedWith.toLocaleString()} shown`;
}
